// © Microsoft Corporation. All rights reserved.

import { ButtonItemType, ButtonType } from "./components/sharing-toolbar/SharingToolbar";
import { LikeDislikeActionKey, TelemetryConstants } from "./ContentActionsToolbar.constants";
import { OptionItemDislike, OptionItemLike, OptionItemUndoDislike, OptionItemUndoLike } from "./components/sharing-toolbar/SharingToolbar.constants";
import { SocialMediaItemConfigAttributes, SocialMediaTarget, ViewsUtility } from "@msnews/views-helpers";
import { Telemetry, TelemetryProps, TelemetryTypes } from "@msnews/experiences-base";

import { Status } from ".";
import { TelemetryBase } from "@msnews/core";

/**
 * Helper class to implement telemetry for content actions toolbar
 * @export
 * @class
 */
export class ContentActionsToolbarTelemetryHelper {

    /**
     * Injects telemetry data into sharing buttons array
     * @param {ButtonItemType[]} sharingButtons The sharing buttons array
     * @param {Status} status The current content status
     * @param {TelemetryProps} sharingToolbarTelemetryProps The sharing toolbar telemetry props
     * @param {TrackTelemetryEventSignature} handleTelemetryCall Dispatches click telemetry events
     */
    public static InjectTelemetry(sharingButtons: ButtonItemType[], status: Status, sharingToolbarTelemetryProps: TelemetryProps): void {
        sharingButtons.map(button => {
            let buttonTelemetryName: string;
            switch (button.buttonType) {
                case ButtonType.SocialMedia:
                    buttonTelemetryName = button.buttonData.mediaType.toUpperCase();
                    button.buttonData.newWindowTelemetryCall = (event: any) => {
                        TelemetryBase.trackEvent({ type: "click", target: event.currentTarget }, null, null, buttonTelemetryName);
                        return;
                    };
                    break;
                case ButtonType.ShareDialog:
                    if (button.buttonData.shareDialogProps && button.buttonData.shareDialogProps.socialMediaItems) {
                        buttonTelemetryName = button.buttonType.toUpperCase();
                        const { shareDialogProps } = button.buttonData;
                        button.buttonData.onSelect = (event: any) => {
                            TelemetryBase.trackEvent({ type: "click_nonnav", target: event.currentTarget }, null, null, buttonTelemetryName);
                            return;
                        };
                        if (button.buttonData.target === SocialMediaTarget.newWindow) {
                            button.buttonData.newWindowTelemetryCall = (event: any) => {
                                TelemetryBase.trackEvent({ type: "click", target: event.currentTarget }, null, null, buttonTelemetryName);
                                return;
                            };
                        }

                        const mediaTelemetryProps: { [s: string]: TelemetryProps } = shareDialogProps.socialMediaItems.reduce(
                            (result, value) => {
                                if (value) {
                                    result[value.toLowerCase()] = Telemetry.getChildTelemetryItem(sharingToolbarTelemetryProps, value.toUpperCase(), TelemetryTypes.Share);
                                    result[value.toLowerCase()].userFacingText = `tmx-stb-sd-${value.toLowerCase()}`;
                                }
                                return result;
                            },
                            {}
                        );
                        shareDialogProps.copyButtonTelemetryProps = Telemetry.getChildTelemetryItem(sharingToolbarTelemetryProps, TelemetryConstants.copyLinkTelemetryName, TelemetryTypes.Share);
                        const closeButtonTelemetryProps = Telemetry.getChildTelemetryItem(sharingToolbarTelemetryProps, TelemetryConstants.closeDialogTelemetryName, TelemetryTypes.Close);
                        shareDialogProps.closeButtonTelemetryTags = Telemetry.render(closeButtonTelemetryProps);
                        shareDialogProps.shareIconTelemetryProps = mediaTelemetryProps;
                        shareDialogProps.socialMediaConfigOverride = shareDialogProps.socialMediaItems.reduce((result, mediaItem) => {
                                let mediaItemOverride: SocialMediaItemConfigAttributes = {};
                                if (shareDialogProps.socialMediaConfigOverride && shareDialogProps.socialMediaConfigOverride[mediaItem]) {
                                    mediaItemOverride = {...shareDialogProps.socialMediaConfigOverride[mediaItem]};
                                }
                                mediaItemOverride.newWindowTelemetryCall = event => TelemetryBase.trackEvent({ type: "click", target: event.currentTarget }, null, null, mediaItem.toUpperCase());
                                result[mediaItem] = mediaItemOverride;
                                return result;
                            }, {}
                        );
                        const shareDialogTelemetryProps = Telemetry.getChildTelemetryItem(sharingToolbarTelemetryProps, TelemetryConstants.shareDialogTelemetryName, TelemetryTypes.Interaction);
                        button.buttonData.shareDialogProps = {
                            ...shareDialogProps,
                            ...Telemetry.render(shareDialogTelemetryProps)

                        };
                        button.buttonData.articleTelemetryTags = Telemetry.render(Telemetry.getChildTelemetryItem(sharingToolbarTelemetryProps, TelemetryConstants.shareArticleTelemetryName, TelemetryTypes.Share));
                    }
                    break;
                case ButtonType.LikeDislike:
                    if (status === Status.Like) {
                        buttonTelemetryName = OptionItemUndoLike.key.toUpperCase();
                    } else if (status === Status.DisLike) {
                        buttonTelemetryName = OptionItemUndoDislike.key.toUpperCase();
                    } else {
                        buttonTelemetryName = OptionItemLike.key.toUpperCase();
                    }
                    break;
                case ButtonType.MoreOptions: {
                    buttonTelemetryName = button.buttonType.toUpperCase();
                    const menuItems = button.buttonData.menuItems;
                    if (status === Status.Like) {
                        menuItems.find(e => e.key === LikeDislikeActionKey.undoliked).telemetryTag = Telemetry.render(Telemetry.getChildTelemetryItem(sharingToolbarTelemetryProps, OptionItemUndoLike.key.toUpperCase(), TelemetryTypes.Unlike));
                        menuItems.find(e => e.key === LikeDislikeActionKey.dislike).telemetryTag = Telemetry.render(Telemetry.getChildTelemetryItem(sharingToolbarTelemetryProps, OptionItemDislike.key.toUpperCase(), TelemetryTypes.Dislike));
                    } else if (status === Status.DisLike) {
                        menuItems.find(e => e.key === LikeDislikeActionKey.undodisliked).telemetryTag = Telemetry.render(Telemetry.getChildTelemetryItem(sharingToolbarTelemetryProps, OptionItemUndoDislike.key.toUpperCase(), TelemetryTypes.Undislike));
                        menuItems.find(e => e.key === LikeDislikeActionKey.like).telemetryTag = Telemetry.render(Telemetry.getChildTelemetryItem(sharingToolbarTelemetryProps, OptionItemLike.key.toUpperCase(), TelemetryTypes.Like));
                    } else {
                        menuItems.find(e => e.key === LikeDislikeActionKey.dislike).telemetryTag = Telemetry.render(Telemetry.getChildTelemetryItem(sharingToolbarTelemetryProps, OptionItemDislike.key.toUpperCase(), TelemetryTypes.Dislike));
                    }
                    break;
                }
                default:
                    break;
            }
            button.telemetryTags = Telemetry.render(ViewsUtility.getChildTelemetryItem(sharingToolbarTelemetryProps, buttonTelemetryName, TelemetryTypes.Share));
            if (button.telemetryTags) {
                // Keeping same telemetry headline data as found today in Prime for AB testing
                button.telemetryTags["data-hl"] = `tmx-stb-${buttonTelemetryName.toLowerCase()}`;
            }
        });
    }

}
